import { Fragment, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import Flex from '@/components/common/Flex';
import Logo from '@/components/common/Logo';
import NavbarVerticalMenu from './NavbarVerticalMenu';
import ToggleButton from './ToggleButton';
import routes, {
  supportRoutes,
  financeRoutes
} from '@/routes/siteMaps';
import { useAiAssistantNavRoutes } from '@/app/Mcp/hooks/useAiAssistantNavRoutes';
import { useStartNewMcpChat } from '@/app/Mcp/hooks/useStartNewMcpChat';
import bgNavbar from '@/assets/img/generic/bg-navbar.png';
import { Nav, Navbar } from 'react-bootstrap';
import { navbarBreakPoint } from '@/config';
import { capitalize } from '@/helpers/utils';
import { useAppContext } from '@/Main';
import NavbarLabel from './NavbarLabel';
import DomainSelector from '@/app/common/components/DomainSelector';
import SeoExtension from './SeoExtension';
import {
  NavbarSidebarModeTabs,
  type SidebarBrowseMode,
} from './NavbarSidebarModeTabs';
import ChargeWalletModal from '@/app/Wallet/components/ChargeWalletModal';
import GiftCodeModal from '@/components/navbar/top/GiftCodeModal';
import useSidebarFeatureIndicator from '@/hooks/useSidebarFeatureIndicator';
import { openAiSupportChat } from '@/components/navbar/openAiSupportChat';

const NavbarVertical = () => {
  let time: ReturnType<typeof setTimeout> | null = null;
  const HTMLClassList = document.getElementsByTagName('html')[0].classList;

  const {
    config: { navbarStyle, isNavbarVerticalCollapsed, showBurgerMenu },
  } = useAppContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const startNewChat = useStartNewMcpChat();
  const [showChargeWalletModal, setShowChargeWalletModal] = useState(false);
  const [showGiftCodeModal, setShowGiftCodeModal] = useState(false);
  const isChatPath = pathname.startsWith('/chat');
  const [sidebarMode, setSidebarMode] = useState<SidebarBrowseMode>(() =>
    isChatPath ? 'ai' : 'main'
  );
  const aiRoutes = useAiAssistantNavRoutes(sidebarMode === 'ai');
  const {
    markAsRead: markSidebarFeatureAsRead,
    isUnread: isSidebarFeatureUnread
  } = useSidebarFeatureIndicator();

  useEffect(() => {
    setSidebarMode(isChatPath ? 'ai' : 'main');
  }, [isChatPath]);

  const baseRoutes = routes.filter(
    route =>
      route.label !== supportRoutes.label &&
      route.label !== financeRoutes.label
  );

  useEffect(() => {
    if (isNavbarVerticalCollapsed) {
      HTMLClassList.add('navbar-vertical-collapsed');
    } else {
      HTMLClassList.remove('navbar-vertical-collapsed');
    }
    return () => {
      HTMLClassList.remove('navbar-vertical-collapsed-hover');
    };
  }, [isNavbarVerticalCollapsed, HTMLClassList]);

  const handleMouseLeave = () => {
    if (time) {
      clearTimeout(time);
    }
    HTMLClassList.remove('navbar-vertical-collapsed-hover');
  };

  const handleMouseEnter = () => {
    if (isNavbarVerticalCollapsed) {
      time = setTimeout(() => {
        HTMLClassList.add('navbar-vertical-collapsed-hover');
      }, 100);
    }
  };

  const handleRouteAction = (action: string) => {
    if (action === 'openChargeWalletModal') {
      setShowChargeWalletModal(true);
    }
    if (action === 'openGiftCodeModal') {
      setShowGiftCodeModal(true);
    }
    if (action === 'mcpNewChat') {
      startNewChat();
    }
    if (action === 'openAiSupportChat') {
      openAiSupportChat();
    }
  };

  return (
    <>
      <Navbar
        expand={navbarBreakPoint}
        className={classNames('navbar-vertical', {
          [`navbar-${navbarStyle}`]: navbarStyle !== 'transparent'
        })}
        variant="light"
      >
        <Flex alignItems="center">
          <ToggleButton />
          <Logo at="navbar-vertical" width={150} />
        </Flex>

        <Navbar.Collapse
          in={showBurgerMenu}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            backgroundImage:
              navbarStyle === 'vibrant'
                ? `linear-gradient(-45deg, rgba(0, 160, 255, 0.86), #0048a2),url(${bgNavbar})`
                : 'none'
          }}
        >
          <div style={{ height: '100vh' }}>
            <DomainSelector />
            <NavbarSidebarModeTabs
              value={sidebarMode}
              onChange={setSidebarMode}
            />
            <div
              style={{
                maxHeight: 'calc(100vh - 10rem)',
              }}
              className="navbar-vertical-content scrollbar"
            >
              {sidebarMode === 'main' ? (
                <Nav className="flex-column" as="ul">
                  {baseRoutes.map(route => (
                    <Fragment key={route.label}>
                      {!(
                        'labelDisable' in route && route.labelDisable
                      ) && (
                        <NavbarLabel label={capitalize(route.label)} />
                      )}
                      <NavbarVerticalMenu
                        routes={route.children ?? []}
                        onRouteAction={handleRouteAction}
                        onRouteVisited={markSidebarFeatureAsRead}
                        isFeatureUnread={isSidebarFeatureUnread}
                        itemClassName={
                          route.label === 'ابزارها'
                            ? 'navbar-tool-item'
                            : undefined
                        }
                      />
                    </Fragment>
                  ))}

                  <Fragment key={financeRoutes.label}>
                    {!financeRoutes.labelDisable && (
                      <NavbarLabel label={capitalize(financeRoutes.label)} />
                    )}
                    <NavbarVerticalMenu
                      routes={financeRoutes.children ?? []}
                      onRouteAction={handleRouteAction}
                      onRouteVisited={markSidebarFeatureAsRead}
                      isFeatureUnread={isSidebarFeatureUnread}
                    />
                  </Fragment>

                  <Fragment key={supportRoutes.label}>
                    {!supportRoutes.labelDisable && (
                      <NavbarLabel label={capitalize(supportRoutes.label)} />
                    )}
                    <NavbarVerticalMenu
                      routes={supportRoutes.children ?? []}
                      onRouteAction={handleRouteAction}
                      onRouteVisited={markSidebarFeatureAsRead}
                      isFeatureUnread={isSidebarFeatureUnread}
                    />
                  </Fragment>
                </Nav>
              ) : (
                <Nav className="flex-column" as="ul">
                  <NavbarVerticalMenu
                    routes={aiRoutes.children ?? []}
                    onRouteAction={handleRouteAction}
                    onRouteVisited={markSidebarFeatureAsRead}
                    isFeatureUnread={isSidebarFeatureUnread}
                  />
                </Nav>
              )}
              <SeoExtension />
            </div>
          </div>
        </Navbar.Collapse>
      </Navbar>
      <ChargeWalletModal
        show={showChargeWalletModal}
        onHide={() => setShowChargeWalletModal(false)}
      />
      <GiftCodeModal
        show={showGiftCodeModal}
        onHide={() => setShowGiftCodeModal(false)}
      />
    </>
  );
};

export default NavbarVertical;
